import { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Formik, isNaN } from 'formik';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import MKBox from 'components/MaterialKit/MKBox';
import MKInput from 'components/MaterialKit/MKInput';
import MKTypography from 'components/MaterialKit/MKTypography';
import Button from 'components/Button';
import { getLocaleMap } from 'utils/locales';

const EditLanguageForm = ({ language, onSave, onCancel, ...props }) => {
  const [lm, setLm] = useState({});
  useEffect(() => {
    getLocaleMap([
      'cancel_button',
      'save_button',
    ])
      .then((response) => {
        setLm(response);
      });
  }, []);

  const getFormValidation = (values) => {
    const { name, google_label, value, priority } = values;
    const errors = {};
    if (!name) {
      errors.name = 'Name required';
    }
    if (!google_label) {
      errors.google_label = 'Google Label required';
    }
    if (!value) {
      errors.google_label = 'Value required';
    }
    if (priority && isNaN(priority)) {
      errors.priority = 'Priority is not a Number';
    }
    return errors;
  };

  const initialValues = useMemo(() => ({
    name: language?.name || '',
    google_label: language?.google_label || '',
    value: language?.value || '',
    priority: language?.priority || '',
  }), [language]);

  return (
    <Formik
      onSubmit={onSave}
      initialValues={initialValues}
      enableReinitialize
      validate={getFormValidation}
      {...props}
    >
      {({ handleChange, handleBlur, handleSubmit, errors, values, isSubmitting, dirty, touched }) => {
        return (
          <MKBox component="form" role="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid container item xs={12}>
                <MKInput
                  name="name"
                  label="Name"
                  value={values.name || ''}
                  onChange={handleChange('name')}
                  onBlur={handleBlur('name')}
                  fullWidth
                />
                <MKTypography variant="caption" color="error">
                  {errors.name}
                </MKTypography>
              </Grid>
              <Grid container item xs={12}>
                <MKInput
                  name="google_label"
                  label="Google Label"
                  value={values.google_label || ''}
                  onChange={handleChange('google_label')}
                  onBlur={handleBlur('google_label')}
                  fullWidth
                />
                <MKTypography variant="caption" color="error">
                  {errors.google_label}
                </MKTypography>
              </Grid>
              <Grid container item xs={12}>
                <MKInput
                  name="value"
                  label="Value"
                  value={values.value || ''}
                  onChange={handleChange('value')}
                  onBlur={handleBlur('value')}
                  fullWidth
                />
                <MKTypography variant="caption" color="error">
                  {errors.value}
                </MKTypography>
              </Grid>
              <Grid container item xs={12}>
                <MKInput
                  name="priority"
                  label="Priority"
                  value={values.priority || ''}
                  onChange={handleChange('priority')}
                  onBlur={handleBlur('priority')}
                  fullWidth
                />
                <MKTypography variant="caption" color="error">
                  {errors.priority}
                </MKTypography>
              </Grid>
            </Grid>
            <Grid container justifyContent="flex-end">
              <Grid item xs={12} md={6} xl={4}>
                <MKTypography variant="caption" color="error">
                  {errors.form}
                  &nbsp;
                </MKTypography>
                <MKBox display="flex">
                  <MKBox display="flex" flex={1}>
                    <Button
                      onClick={onCancel}
                      variant="outlined"
                      color="secondary"
                      fullWidth
                    >
                      {lm.cancel_button}
                    </Button>
                  </MKBox>
                  <MKBox display="flex" flex={1} ml={2}>
                    <Button
                      type="submit"
                      variant="gradient"
                      color="info"
                      disabled={isSubmitting || !dirty}
                      fullWidth
                    >
                      {isSubmitting ? (
                        <CircularProgress color="white" size={24} sx={{ position: 'absolute' }} />
                      ) : lm.save_button }
                    </Button>
                  </MKBox>
                </MKBox>
              </Grid>
            </Grid>
          </MKBox>
        );
      }}
    </Formik>
  );
};

EditLanguageForm.propTypes = {
  language: PropTypes.shape({
    name: PropTypes.string,
    google_label: PropTypes.string,
    value: PropTypes.string,
    priority: PropTypes.number,
  }),
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
};

EditLanguageForm.defaultProps = {
  language: {},
  onSave: () => {},
  onCancel: () => {},
};

export default EditLanguageForm;
